import {
  ComcastErrorType,
  getScaledResolution,
  triggerSentryError,
} from '../../../../../shared/helpers'
import {
  getSchedulesByStation,
  getStationDetails,
  setStation,
} from '../../../actions/contentActions'
import { getStationDonationAmountsHelper } from '../../../actions/tvaultActions'
import { Station } from '../../../screens/Livestream/types'
import { store } from '../../../store'
import googleAnalytics4 from '../../googleAnalytics'
import { FavoriteStation } from './types'

declare const COMPILING_FOR_COMCASTX1: boolean
declare const COMCAST_IN_APP_DONATIONS_ENABLED: boolean

type StationData = {
  call_sign?: string
  mvod_enabled?: boolean
  cid?: string
  title?: string
  images?: Record<string, string>
  short_common_name?: string
  passport_learn_more_url?: string
}

export type ExtractedStation = {
  call_sign?: string
  passportEligible?: boolean
  station_id?: string
  station_title?: string
  black_logo?: string
  color_logo?: string
  station_scenery: string
  white_logo?: string
  white_single_logo: string
  white_cobranded_logo: string
  short_common_name?: string
  passport_learn_more_url?: string
  showMenu: null
}

function mapStationData(data: StationData): ExtractedStation {
  return {
    call_sign: data?.call_sign,
    passportEligible: data?.mvod_enabled,
    station_id: data?.cid,
    station_title: data?.title,
    black_logo: data?.images?.['black-logo'] || '',
    color_logo: data?.images?.['color-logo'] || '',
    station_scenery: `${data?.images?.['station-scenery-11']}?resize=${(() => {
      const { width, height } = getScaledResolution()
      return `${width}x${height}`
    })()}&format=webp`,
    white_logo: data?.images?.['white-logo'] || '',
    white_single_logo: data?.images?.['white_single_logo']
      ? `${data.images['white_single_logo']}?format=webp`
      : '',
    white_cobranded_logo: `${data?.images?.['white-cobranded-logo']}?format=webp`,
    short_common_name: data?.short_common_name,
    passport_learn_more_url: data?.passport_learn_more_url,
    showMenu: null,
  }
}

export function extractAndReturnStation(station: Station): ExtractedStation {
  return mapStationData(station.resource)
}

export function extractAndReturnFavoriteStation(
  favoriteStations: FavoriteStation
): ExtractedStation {
  const content = favoriteStations?.collections?.[0]?.content?.[0]
  return mapStationData(content)
}

export async function setUserStationById(stationId: string): Promise<boolean> {
  try {
    await setStation(stationId)

    const favoriteStation = await getStationDetails(stationId)
    const formattedStation = extractAndReturnStation(favoriteStation)

    if (!favoriteStation) {
      throw new Error('No favorite station found after setting user station')
    }

    store.dispatch({
      type: 'UPDATE_STATION',
      payload: formattedStation,
    })

    store.dispatch({
      type: 'UPDATE_USER_STATION',
      payload: formattedStation.call_sign,
    })

    store.dispatch({
      type: 'SET_HOME_CONTENT',
      payload: { collections: null },
    })

    //Todo: Type declarations here. Declare Global types.
    if (COMPILING_FOR_COMCASTX1 && COMCAST_IN_APP_DONATIONS_ENABLED) {
      getStationDonationAmountsHelper(formattedStation.call_sign || '')
    }

    googleAnalytics4.setUserProperty(
      'station_localization',
      formattedStation.call_sign || ''
    )

    return true
  } catch (err: any) {
    console.error('Error setting user station: ', err)
    triggerSentryError('setStation', 'stationUtils.ts', {
      errorMessage: err.message ?? 'Undefined error message (default)',
      pbs_id: stationId,
      errorLocation: 'setUserStationById',
      errorType: ComcastErrorType.other,
    })
    return false
  }
}

export async function loadStationSchedules(
  stationId: string,
  stationData: Station = {
    callsign: '',
    cid: '',
    city: '',
    fullName: '',
    shortName: '',
    resource: {},
  },
  updateFallbackStation = false
): Promise<boolean> {
  if (updateFallbackStation) {
    store.dispatch({
      type: 'UPDATE_FALLBACK_STATION',
      payload: {
        ...stationData,
        cid: stationId,
      },
    })
  }

  await getSchedulesByStation(
    stationId,
    new Date().toISOString().split('T')[0],
    true
  )

  return true
}
